import React, { ElementType, type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, type SxProps, type Theme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import IndicatorChip, { IndicatorChipProps, IndicatorChipVariantTranslated } from '@/components/statuses/IndicatorChip';
import withFormControl from '@/hoc/withFormControl';
import { useLastValidValue } from '@/hooks/useLastValidValue';

interface ChipMultiSelectProps {
  id?: string;
  name?: string;
  value?: (string | number)[];
  onChange?: (newValue: (string | number)[] | null) => void;
  options?: IndicatorChipVariantTranslated[];
  sx?: SxProps<Theme>;
  className?: string;
  ChipComponent?: ElementType<IndicatorChipProps & { value: string | number | null }>;
  enforcedValue?: boolean;
  disabled?: boolean;
  maxOptions?: number;
}

const ChipMultiSelectField: FC<ChipMultiSelectProps> = ({
  id,
  value = [],
  onChange,
  options = [],
  sx,
  className,
  ChipComponent,
  enforcedValue = false,
  disabled = false,
  maxOptions
}) => {
  const isNotEmpty = useCallback(
    (value: (string | number)[] | null) => !!value && Array.isArray(value) && !!value?.length && value?.some(Boolean),
    []
  );
  const [selectedChips, setSelectedChips] = useState<(string | number)[]>(value || []);
  const lastNonEmptyValue = useLastValidValue(value, isNotEmpty);

  const fallbackValue = useMemo(() => {
    const fallbackOption = options?.[0]?.value;
    const resolvedValue: (string | number)[] = fallbackOption && enforcedValue ? [fallbackOption] : [];

    return lastNonEmptyValue || resolvedValue;
  }, [enforcedValue, lastNonEmptyValue, options]);

  useEffect(() => {
    const shouldUseFallback = !isNotEmpty(value);
    setSelectedChips(shouldUseFallback ? fallbackValue : value);
    if (shouldUseFallback && fallbackValue !== value) {
      onChange?.(fallbackValue);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChipClick = useCallback(
    (chipValue: string | number | null) => {
      if (enforcedValue && !chipValue) {
        return;
      }

      if (!chipValue) {
        setSelectedChips([]);
        onChange?.(null);

        return;
      }

      const currentIndex = selectedChips.indexOf(chipValue);
      const newSelectedChips = [...selectedChips];

      if (currentIndex === -1) {
        if (typeof maxOptions === 'number' && newSelectedChips.length >= maxOptions) {
          return;
        }
        newSelectedChips.push(chipValue);
      } else {
        newSelectedChips.splice(currentIndex, 1);
      }

      setSelectedChips(newSelectedChips);
      onChange?.(newSelectedChips);
    },
    [onChange, selectedChips, enforcedValue, maxOptions]
  );

  const ResolvedChipComponent = useMemo(() => ChipComponent || IndicatorChip, [ChipComponent]);

  return (
    <Stack
      id={id}
      direction="row"
      flexWrap="wrap"
      gap={1}
      sx={mergeSx(
        {
          filter: disabled ? 'grayscale(100%)' : 'none',
          pointerEvents: disabled ? 'none' : 'auto'
        },
        sx
      )}
      className={className}
    >
      {(options || []).map(option => {
        const isSelected = !option.value ? !selectedChips.length : selectedChips.includes(option.value);

        return (
          <ResolvedChipComponent
            key={option.value}
            value={option.value}
            sx={option.sx}
            label={option.label}
            onClick={() => handleChipClick(option.value)}
            icon={option.Icon}
            isSelected={isSelected}
          />
        );
      })}
    </Stack>
  );
};

const ChipMultiSelect = withFormControl<(string | number)[] | null, (string | number)[] | null, ChipMultiSelectProps>(
  ChipMultiSelectField
);

export default ChipMultiSelect;
